import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { useDispatch } from "src/redux/store";
import useAuth from "src/hooks/useAuth";
import { setAddOnQuantity } from "src/redux/slices/addOns";
import { completeOnboardingStep } from "src/redux/slices/onboarding";

// ----------------------------------------------------------------------
//   Handler post-Stripe Checkout. Cuando el user vuelve al portal
//   después de pagar (Stripe redirige a /app/user/account/subs_success),
//   este componente detecta el flag `tmo_after_checkout_apply_addons`
//   guardado por `StepCheckout` y aplica cada add-on pendiente con
//   `SetAddOn(id, 1)`. Después limpia el flag + el `pendingSelection`
//   y marca el step `checkout` del onboarding como completado.
//
//   Se monta en `DashboardLayout` para que se dispare
//   automáticamente en cualquier aterrizaje. Idempotente — si no hay
//   flag, no hace nada.
// ----------------------------------------------------------------------

const AFTER_CHECKOUT_KEY = "tmo_after_checkout_apply_addons";

export default function PostCheckoutAutoApply() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user, isAuthenticated } = useAuth();
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    if (!isAuthenticated || !user) return;
    // La sub debe estar activa para que SetAddOn no reviente con
    // NO_ACTIVE_SUBSCRIPTION. Si Stripe aún no confirmó el webhook,
    // esperamos al siguiente render (paymentConfirmation refresca al
    // hacer refreshTokenData desde otras acciones).
    const hasActiveSub = !!user?.paymentConfirmation?.[0]?.isActive;
    if (!hasActiveSub) return;

    let raw = null;
    try {
      raw = localStorage.getItem(AFTER_CHECKOUT_KEY);
    } catch {
      return;
    }
    if (!raw) return;

    let payload = null;
    try {
      payload = JSON.parse(raw);
    } catch {
      try { localStorage.removeItem(AFTER_CHECKOUT_KEY); } catch { /* noop */ }
      return;
    }

    consumedRef.current = true;
    const addOnIds = Array.isArray(payload?.addOnIds) ? payload.addOnIds : [];

    (async () => {
      let ok = 0;
      let failed = 0;
      for (const id of addOnIds) {
        try {
          await dispatch(setAddOnQuantity(id, 1));
          ok++;
        } catch {
          failed++;
        }
      }
      try { localStorage.removeItem(AFTER_CHECKOUT_KEY); } catch { /* noop */ }
      // NO limpiamos `pendingSelection` — así el user puede reabrir el
      // wizard sin perder su selección si algo va mal. La sub activa
      // ya bloquea el `hasPendingCheckout` en el hook, así que
      // preservar la selección no molesta y sirve de red de seguridad.
      // Marca el step `checkout` como completado — el modal wizard
      // saltará al `StepFinish` cuando se re-abra.
      try {
        await dispatch(completeOnboardingStep("checkout", {
          dataJson: JSON.stringify({ ok, failed, appliedAt: new Date().toISOString() }),
        }));
      } catch { /* noop */ }
      if (ok > 0 || failed > 0) {
        enqueueSnackbar(
          failed > 0
            ? `Suscripción activada · ${ok} extras aplicados, ${failed} fallaron`
            : `Suscripción activada · ${ok} extras aplicados`,
          { variant: failed > 0 ? "warning" : "success" },
        );
      }
    })();
  }, [dispatch, isAuthenticated, user, enqueueSnackbar]);

  return null;
}
